/**
 * Real implementations for the 10 PHP-parity sidebar entries that
 * previously routed to PlannedFeaturePage. Each backed by an endpoint
 * in `packages/backend/src/modules/parity/parity.routes.ts`
 * (mounted at /api/admin/parity/*).
 */
import { Card, Col, Row, Statistic, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';

const fmtNum = (v: string | number | undefined | null) => {
  if (v === null || v === undefined || v === '') return '0';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isNaN(n) ? '0' : n.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
};

interface PageEnvelope<T> {
  total: number;
  page: number;
  page_size: number;
  rows: T[];
}

function usePaged<T>(path: string) {
  return useQuery<PageEnvelope<T>>({
    queryKey: ['parity', path],
    queryFn: async () => api.get<PageEnvelope<T>>(path, { page: 1, page_size: 50 }),
    staleTime: 30_000,
  });
}

/* ---------------- private letter ---------------- */
export function ParityPrivateLetterPage() {
  const { data, isLoading } = usePaged<{
    id: number; user_id: number; title: string; status: string; create_time: string;
  }>('/api/admin/parity/private-letter');
  return (
    <Card title="私信管理（旧 user.PrivateLetter）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          { title: '用户', dataIndex: 'user_id', width: 100 },
          { title: '标题', dataIndex: 'title' },
          { title: '状态', dataIndex: 'status', width: 120, render: (v: string) => <Tag>{v}</Tag> },
          { title: '创建时间', dataIndex: 'create_time', width: 180 },
        ]}
      />
    </Card>
  );
}

/* ---------------- opinion ---------------- */
export function ParityOpinionPage() {
  const { data, isLoading } = usePaged<{
    id: number; user_id: number; title: string; status: string; create_time: string;
  }>('/api/admin/parity/opinion');
  return (
    <Card title="意见建议（旧 uo.opinion）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          { title: '用户', dataIndex: 'user_id', width: 100 },
          { title: '标题', dataIndex: 'title' },
          { title: '状态', dataIndex: 'status', width: 120, render: (v: string) => <Tag>{v}</Tag> },
          { title: '提交时间', dataIndex: 'create_time', width: 180 },
        ]}
      />
    </Card>
  );
}

/* ---------------- ops snapshot ---------------- */
export function ParityOpsSnapshotPage() {
  const { data } = useQuery<any>({
    queryKey: ['parity-ops-snapshot'],
    queryFn: async () => api.get<any>('/api/admin/parity/ops-snapshot'),
    staleTime: 30_000,
  });
  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card size="small"><Statistic title="总用户" value={data?.users?.total ?? 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="真人用户" value={data?.users?.real ?? 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="虚拟用户" value={data?.users?.bots ?? 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="EP 在挂订单" value={data?.ep_open ?? 0} /></Card></Col>
      </Row>
      <Row gutter={16}>
        <Col span={6}><Card size="small"><Statistic title="近 24h 充值 (CNY)" value={fmtNum(data?.recharge_24h_cny)} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="近 24h 提现 (CNY)" value={fmtNum(data?.withdraw_24h_cny)} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="近 24h EP 成交" value={data?.ep_closed_24h ?? 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="累计捐赠 (ORB)" value={fmtNum(data?.donation_total_uo)} /></Card></Col>
      </Row>
    </div>
  );
}

/* ---------------- bonus trade log ---------------- */
export function ParityBonusTradeLogPage() {
  const { data, isLoading } = usePaged<{
    id: string; seller_id: number; buyer_id: number | null;
    amount_ep: string; price_cny: string; status: string; settled_at: string;
  }>('/api/admin/parity/bonus-trade-log');
  return (
    <Card title="挂买挂卖记录（旧 uo.bonusLog）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: '订单 ID', dataIndex: 'id', width: 280 },
          { title: '卖家', dataIndex: 'seller_id', width: 100 },
          { title: '买家', dataIndex: 'buyer_id', width: 100 },
          { title: 'EP 数量', dataIndex: 'amount_ep', width: 140, render: fmtNum },
          { title: '单价 CNY', dataIndex: 'price_cny', width: 120, render: fmtNum },
          { title: '状态', dataIndex: 'status', width: 100, render: (v: string) => <Tag color="green">{v}</Tag> },
          { title: '结算时间', dataIndex: 'settled_at', width: 180 },
        ]}
      />
    </Card>
  );
}

/* ---------------- pi settlement ---------------- */
export function ParityPiSettlementPage() {
  const { data, isLoading } = usePaged<{
    id: string; user_id: number; asset: string; action: string;
    delta: string; balance_after: string; ref: string; create_time: string;
  }>('/api/admin/parity/pi-settlement');
  return (
    <Card title="沉淀奖金转币（旧 uo.pi）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: '账目 ID', dataIndex: 'id', width: 100 },
          { title: '用户', dataIndex: 'user_id', width: 100 },
          { title: '资产', dataIndex: 'asset', width: 80 },
          { title: '动作', dataIndex: 'action', width: 160 },
          { title: '增减', dataIndex: 'delta', width: 140, render: fmtNum },
          { title: '结算后余额', dataIndex: 'balance_after', width: 160, render: fmtNum },
          { title: 'ref', dataIndex: 'ref' },
          { title: '时间', dataIndex: 'create_time', width: 180 },
        ]}
      />
    </Card>
  );
}

/* ---------------- account config log ---------------- */
export function ParityAccountConfigLogPage() {
  const { data, isLoading } = usePaged<{
    id: string; admin_id: number | null; admin_name: string | null;
    action: string; target_type: string | null; target_id: string | null;
    body_preview: string | null; result: string; create_time: string;
  }>('/api/admin/parity/account-config-log');
  return (
    <Card title="账户沉淀记录（旧 uo.ConfigLog）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: '日志 ID', dataIndex: 'id', width: 100 },
          { title: '操作员', dataIndex: 'admin_name', width: 140 },
          { title: '动作', dataIndex: 'action', width: 200 },
          { title: '目标', width: 200, render: (_: any, r) => `${r.target_type ?? '-'}:${r.target_id ?? '-'}` },
          { title: '结果', dataIndex: 'result', width: 80, render: (v: string) => <Tag color={v === 'ok' ? 'green' : 'red'}>{v}</Tag> },
          { title: '摘要', dataIndex: 'body_preview', ellipsis: true },
          { title: '时间', dataIndex: 'create_time', width: 180 },
        ]}
      />
    </Card>
  );
}

/* ---------------- uo count report ---------------- */
export function ParityUoCountReportPage() {
  const { data } = useQuery<{ tier: string; users: number; total_uo: string; total_ep: string }[]>({
    queryKey: ['parity-uo-count-report'],
    queryFn: async () =>
      api.get<{ tier: string; users: number; total_uo: string; total_ep: string }[]>(
        '/api/admin/parity/uo-count-report',
      ),
    staleTime: 30_000,
  });
  return (
    <Card title="0419 版指导销售统计（按等级 tier 聚合）" size="small">
      <Table
        size="small"
        dataSource={data ?? []}
        rowKey="tier"
        pagination={false}
        columns={[
          { title: 'Tier', dataIndex: 'tier', width: 80, render: (v: string) => <Tag color="blue">{v}</Tag> },
          { title: '用户数', dataIndex: 'users', width: 120 },
          { title: 'ORB 持有合计', dataIndex: 'total_uo', width: 200, render: fmtNum },
          { title: 'EP 持有合计', dataIndex: 'total_ep', width: 200, render: fmtNum },
        ]}
      />
    </Card>
  );
}

/* ---------------- uoc mint log ---------------- */
export function ParityUocMintLogPage() {
  const { data, isLoading } = usePaged<{
    id: string; user_id: number; delta: string; balance_after: string; ref: string; create_time: string;
  }>('/api/admin/parity/uoc-mint-log');
  return (
    <Card title="UOC 铸造记录（旧 uo.userBalanceLogz）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: '日志 ID', dataIndex: 'id', width: 120 },
          { title: '用户', dataIndex: 'user_id', width: 100 },
          { title: '铸造数量', dataIndex: 'delta', width: 160, render: fmtNum },
          { title: '余额', dataIndex: 'balance_after', width: 160, render: fmtNum },
          { title: 'ref', dataIndex: 'ref' },
          { title: '时间', dataIndex: 'create_time', width: 180 },
        ]}
      />
    </Card>
  );
}

/* ---------------- uoc withdraw log ---------------- */
export function ParityUocWithdrawLogPage() {
  const { data, isLoading } = usePaged<{
    id: string; user_id: number; delta: string; balance_after: string; ref: string; create_time: string;
  }>('/api/admin/parity/uoc-withdraw-log');
  return (
    <Card title="UOC 提币记录（旧 uo.userBalanceLogt）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: '日志 ID', dataIndex: 'id', width: 120 },
          { title: '用户', dataIndex: 'user_id', width: 100 },
          { title: '提币数量', dataIndex: 'delta', width: 160, render: fmtNum },
          { title: '余额', dataIndex: 'balance_after', width: 160, render: fmtNum },
          { title: 'ref', dataIndex: 'ref' },
          { title: '时间', dataIndex: 'create_time', width: 180 },
        ]}
      />
    </Card>
  );
}

/* ---------------- upload manage ---------------- */
export function ParityUploadManagePage() {
  const { data, isLoading } = usePaged<{
    id: number; filename: string; original_name: string; mime_type: string;
    size_bytes: number; url: string; category: string; create_time: string;
  }>('/api/admin/parity/upload-manage');
  return (
    <Card title="上传素材管理（旧 system.uploadfile）" size="small">
      <Table
        size="small"
        loading={isLoading}
        dataSource={data?.rows ?? []}
        rowKey="id"
        pagination={{ total: data?.total ?? 0, pageSize: 50 }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          { title: '文件名', dataIndex: 'original_name' },
          { title: '类型', dataIndex: 'mime_type', width: 160 },
          { title: '分类', dataIndex: 'category', width: 120, render: (v: string) => <Tag>{v}</Tag> },
          {
            title: '大小',
            dataIndex: 'size_bytes',
            width: 120,
            render: (v: number) => `${(v / 1024).toFixed(1)} KB`,
          },
          {
            title: '链接',
            dataIndex: 'url',
            ellipsis: true,
            render: (v: string) => (
              <a href={v} target="_blank" rel="noreferrer">
                {v}
              </a>
            ),
          },
          { title: '上传时间', dataIndex: 'create_time', width: 180 },
        ]}
      />
    </Card>
  );
}
